import { useMutation, useQuery } from '@apollo/client'
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Switch,
    Typography,
} from '@mui/material'
import FormHelperText from '@mui/material/FormHelperText'
import StarterKit from '@tiptap/starter-kit'
import { GetNotificationsQuery } from '__generated__/graphql'
import { StyledContainer } from 'candidate/components/styles'
import { usePageTitle } from 'shared/hooks/usePageTitle'
import theme from 'candidate/theme'
import { formatDistanceToNow } from 'date-fns'
import { RichTextReadOnly } from 'mui-tiptap'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { LoadingPanel } from 'shared/components/LoadingPanel'
import useToast from 'shared/components/Toast/useToast'
import {
    GetNotificationPreferences,
    GetNotifications,
    MarkNotificationsAsRead,
    UpdateNotificationPreferences,
} from 'shared/graphql/notifications.gql'
import { useNotifications } from 'shared/hooks/useNotification'
import { getPxAsRem } from 'shared/utils'
import { ColumnContainer, StyledFormControlLabel } from './styled'

const Notifications: React.FC = () => {
    usePageTitle('Notifications')
    const { notifications, dispatchNotifications } = useNotifications()
    const [notificationData, setNotificationData] = useState<
        GetNotificationsQuery['notifications']['data']
    >([])
    const [notificationIds, setNotificationIds] = useState<string[]>([])
    const [notificationPreferences, setNotificationPreferences] = useState({
        emailOptIn: false,
    })
    const [isFetching, setIsFetching] = useState(false)
    const [pageTotal, setPageTotal] = useState(0)
    const currentPageRef = useRef(1)
    const observer = useRef<IntersectionObserver | null>(null)
    const toast = useToast()
    const { loading, error, fetchMore } = useQuery(GetNotifications, {
        fetchPolicy: 'network-only',
        variables: { page: 1, limit: 10 },
        onCompleted(data) {
            setNotificationData(data.notifications.data)
            setNotificationIds(
                data.notifications.data.map((notification) => notification.id)
            )
            setPageTotal(data.notifications.pageInfo.pageTotal)
            dispatchNotifications({
                type: 'SET_NOTIFICATIONS',
                payload: data.notifications.data,
            })
        },
        onError(error) {
            console.log('Error with GetNotifications:', error)
            toast({
                title: 'Something went wrong',
                message: 'Please try again later',
            })
        },
    })

    useQuery(GetNotificationPreferences, {
        fetchPolicy: 'network-only',
        onCompleted(data) {
            setNotificationPreferences({
                emailOptIn: data.notificationPreferences.emailOptIn,
            })
        },
        onError(error) {
            console.error('Error fetching notification preferences', error)
            toast({
                title: 'Something went wrong',
                message: 'Please try again later',
            })
        },
    })

    const [markNotificationsAsRead] = useMutation(MarkNotificationsAsRead, {
        onCompleted(data) {
            if (data.markNotificationsAsRead.success) {
                toast({
                    severity: 'success',
                    title: 'Success!',
                    message: 'Notifications marked as read!',
                })
            }
        },
        onError(error) {
            console.error('Error marking notifications as read', error)
            toast({
                title: 'Something went wrong',
                message: error.message,
            })
        },
    })

    const [updateNotificationPreferences] = useMutation(
        UpdateNotificationPreferences,
        {
            onCompleted(data) {
                if (data.updateNotificationPreferences.success) {
                    toast({
                        severity: 'success',
                        title: 'Success!',
                        message: 'Notification preferences updated!',
                    })
                }
            },
            onError(error) {
                console.error('Error updating notification preferences', error)
                toast({
                    title: 'Something went wrong',
                    message: 'Please try again later',
                })
            },
        }
    )

    const submitNotificationsAsRead = async (uuids: string[]) => {
        try {
            await markNotificationsAsRead({ variables: { ids: uuids } })
            uuids.forEach((id) =>
                dispatchNotifications({ type: 'MARK_AS_READ', payload: id })
            )
            // Update state to mark provided notifications as read
            setNotificationData((prevData) =>
                prevData.map((notification) =>
                    uuids.includes(notification.id)
                        ? { ...notification, isRead: true }
                        : notification
                )
            )
        } catch (err) {
            console.log('Error marking notifications as read', err)
        }
    }

    const submitNotificationPreferences = async (emailOptIn: boolean) => {
        try {
            await updateNotificationPreferences({
                variables: {
                    emailOptIn,
                },
            })
            setNotificationPreferences({
                emailOptIn,
            })
        } catch (err) {
            console.log('Error submitting preferences', err)
        }
    }

    const loadMoreNotifications = useCallback(() => {
        if (loading || isFetching || currentPageRef.current === pageTotal)
            return

        setIsFetching(true)
        const nextPage = currentPageRef.current + 1
        console.log(`Loading more notifications for page ${nextPage}...`)
        fetchMore({
            variables: {
                page: nextPage,
                limit: 10,
            },
            updateQuery: (prevResult, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prevResult
                currentPageRef.current = nextPage
                setIsFetching(false)
                dispatchNotifications({
                    type: 'SET_NOTIFICATIONS',
                    payload: [
                        ...prevResult.notifications.data,
                        ...fetchMoreResult.notifications.data,
                    ],
                })
                setNotificationData((prevData) => [
                    ...prevData,
                    ...fetchMoreResult.notifications.data,
                ])
                setNotificationIds(
                    fetchMoreResult.notifications.data.map(
                        (notification) => notification.id
                    )
                )
                return fetchMoreResult
            },
        })
    }, [fetchMore, isFetching, loading, pageTotal, dispatchNotifications])

    useEffect(() => {
        if (observer.current) observer.current.disconnect()

        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                console.log('Loading more items...')
                loadMoreNotifications()
            }
        })

        return () => {
            if (observer.current) observer.current.disconnect()
        }
    }, [loadMoreNotifications])

    useEffect(() => {
        if (notificationData.length === 0) return
        const lastNotificationElement = document.querySelector(
            `.notification-card:last-child`
        )
        if (lastNotificationElement) {
            observer.current?.observe(lastNotificationElement)
        }
        return () => {
            if (lastNotificationElement) {
                observer.current?.unobserve(lastNotificationElement)
            }
        }
    }, [notificationData, notifications])

    const BulletPoint = (
        <Box
            component="span"
            sx={{
                display: 'inline-block',
                mx: getPxAsRem(2),
                transform: 'scale(0.8)',
            }}
        >
            •
        </Box>
    )
    const unreadNotifications = notificationData.filter(
        (notification) => !notification.isRead
    )

    if (loading && currentPageRef.current === 1) return <LoadingPanel />

    if (error) return <Typography>Error loading notifications</Typography>

    return (
        <StyledContainer>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                }}
            >
                <Typography variant="h2">Notifications</Typography>

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{ marginRight: getPxAsRem(12) }}
                    >
                        {unreadNotifications.length} unread
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={unreadNotifications.length === 0}
                        onClick={() =>
                            submitNotificationsAsRead(notificationIds)
                        }
                    >
                        Mark all as read
                    </Button>
                </div>
            </div>
            <Card style={{ marginTop: getPxAsRem(28) }}>
                <CardContent>
                    <Typography variant="h5" gutterBottom>
                        Notifications
                    </Typography>
                </CardContent>
                <CardActions>
                    <ColumnContainer>
                        <StyledFormControlLabel
                            control={
                                <Switch
                                    checked={notificationPreferences.emailOptIn}
                                />
                            }
                            label={
                                notificationPreferences.emailOptIn
                                    ? 'Opt out'
                                    : 'Opt in'
                            }
                            labelPlacement="end"
                            onChange={() =>
                                submitNotificationPreferences(
                                    !notificationPreferences.emailOptIn
                                )
                            }
                        />
                        <FormHelperText>EMAIL</FormHelperText>
                    </ColumnContainer>
                </CardActions>
            </Card>
            {notificationData.map((item, index) => (
                <Card
                    key={item.id}
                    className="notification-card"
                    style={{ marginTop: getPxAsRem(28) }}
                >
                    <CardContent>
                        {item.isRead ? (
                            <Typography
                                variant="body1"
                                color={theme.palette.primary.dark}
                            >
                                Read
                            </Typography>
                        ) : (
                            <Typography
                                variant="body1"
                                color={theme.palette.primary.main}
                            >
                                Unread{BulletPoint}
                                {formatDistanceToNow(new Date(item.updatedAt), {
                                    addSuffix: true,
                                })}
                            </Typography>
                        )}
                        <Typography variant="h4" gutterBottom>
                            {item.notificationData?.subject}
                        </Typography>
                        <RichTextReadOnly
                            extensions={[StarterKit]}
                            content={item.notificationData?.content}
                        />
                    </CardContent>
                    <CardActions>
                        {item.notificationData?.linkText && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() =>
                                    submitNotificationsAsRead([item.id])
                                }
                                href={
                                    item.notificationData?.linkHref || undefined
                                }
                                size="medium"
                            >
                                {item.notificationData.linkText
                                    ? item.notificationData.linkText
                                    : 'Learn More'}
                            </Button>
                        )}
                        {item.isRead ? null : (
                            <Button
                                variant="outlined"
                                onClick={() =>
                                    submitNotificationsAsRead([item.id])
                                }
                                color="primary"
                                size="medium"
                            >
                                Mark As Read
                            </Button>
                        )}
                    </CardActions>
                </Card>
            ))}
            {isFetching && <Typography>Loading more...</Typography>}
        </StyledContainer>
    )
}

export default Notifications
